import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Globe, Mail, Phone, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SearchBar from '@/components/SearchBar';
import TopHeroTemplate from '@/components/layout/TopHeroTemplate';
import { useAuthState } from '@/hooks/useAuthState';
import { PartnerService, Partner } from '@/services/partnerService';
import PartnerVouchers from '@/components/PartnerVouchers';
import { getBannerImageUrl, getPartnerLogoUrl } from '@/config/api';
import SocialShare from '@/components/SocialShare';
import { BannerService, Banner } from '@/services/bannerService';
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import heroMuroAlto2 from "@/assets/hero-muro-alto-2.jpg";
import heroMuroAlto3 from "@/assets/hero-muro-alto-3.jpg";
import heroMuroAlto4 from "@/assets/hero-muro-alto-4.jpg";
import heroMuroAlto5 from "@/assets/hero-muro-alto-5.jpg";

const PartnerDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [principalBanners, setPrincipalBanners] = useState<Banner[]>([]);

  // Array de imagens de Muro Alto para o banner rotativo
  const heroImages = [heroMuroAlto1, heroMuroAlto2, heroMuroAlto3, heroMuroAlto4, heroMuroAlto5];
  
  // Seleciona uma imagem aleatória a cada render (refresh da página)
  const randomHeroImage = useMemo(() => {
    return heroImages[Math.floor(Math.random() * heroImages.length)];
  }, []);
  const randomPrincipalBanner = useMemo(() => {
    if (principalBanners.length > 0) {
      return principalBanners[Math.floor(Math.random() * principalBanners.length)];
    }
    return null;
  }, [principalBanners]);

  useEffect(() => {
    const loadPrincipalBanners = async () => {
      try {
        const principal = await BannerService.getBannersByType('principal');
        setPrincipalBanners(principal);
      } catch (err) {
        console.error('Erro ao carregar banner principal na página de parceiro:', err);
      }
    };
    loadPrincipalBanners();
  }, []);

  useEffect(() => {
    const loadPartner = async () => {
      if (!id) {
        setError('ID do parceiro não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const partnerData = await PartnerService.getPartnerById(parseInt(id));
        
        if (partnerData) {
          setPartner(partnerData);
        } else {
          setError('Parceiro não encontrado');
        }
      } catch (error) {
        console.error('Erro ao carregar parceiro:', error);
        setError('Erro ao carregar informações do parceiro');
      } finally {
        setLoading(false);
      }
    };

    loadPartner();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleWebsiteClick = () => {
    if (partner?.website) {
      window.open(partner.website, '_blank');
    }
  };

  const handleEmailClick = () => {
    if (partner?.email) {
      window.location.href = `mailto:${partner.email}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar 
          isAuthenticated={isAuthenticated} 
          isOwner={userType === "owner"} 
          userName={userName}
          userPhoto={userPhoto}
          userId={userId}
          userType={userType}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando informações do parceiro...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar 
          isAuthenticated={isAuthenticated} 
          isOwner={userType === "owner"} 
          userName={userName}
          userPhoto={userPhoto}
          userId={userId}
          userType={userType}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Parceiro não encontrado</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={handleBack} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopHeroTemplate
        backgroundImage={
          randomPrincipalBanner
            ? getBannerImageUrl(randomPrincipalBanner.image_path)
            : randomHeroImage
        }
        backgroundColor={randomPrincipalBanner?.background_color || "transparent"}
        navbarProps={{
          isAuthenticated,
          isOwner: userType === "owner",
          userName,
          userPhoto,
          userId,
          userType,
          cleanHeader: true,
          rightActionsCard: true,
        }}
        sectionClassName="h-[44vh] min-h-[430px] sm:min-h-[460px] flex items-end pb-8"
        contentClassName="relative z-10 container mx-auto px-4 text-center mt-28 sm:mt-32 md:mt-24"
      >
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-3 drop-shadow-lg">
          Viva sua melhor experiência
        </h1>
        <p className="text-base sm:text-xl md:text-2xl text-white/95 mb-8 drop-shadow-md">
          Descubra nossa rede de parceiros oficiais
        </p>

        <SearchBar />
      </TopHeroTemplate>

      {/* Header do Parceiro */}
      <div className="bg-primary/5 py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <Button onClick={handleBack} variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <img
                src={getPartnerLogoUrl(partner.logo)}
                alt={partner.name}
                className="w-32 h-32 object-contain rounded-lg shadow-medium"
                onError={(e) => {
                  console.error('Erro ao carregar logo:', partner.logo);
                  e.currentTarget.src = '/placeholder.svg';
                }}
              />
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-4 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                  {partner.name}
                </h1>
                <SocialShare
                  url={`/parceiro/${partner.id}`}
                  title={partner.name}
                  description={partner.description || undefined}
                  type="partner"
                />
              </div>
              <p className="text-lg text-muted-foreground">
                Parceiro oficial do Porto 2 Life Resort
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações de Contato */}
          <div className="lg:col-span-1">
            <Card className="shadow-medium">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-primary" />
                  Informações de Contato
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {partner.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Endereço</p>
                      <p className="text-sm text-muted-foreground">{partner.address}</p>
                    </div>
                  </div>
                )}
                
                {partner.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Website</p>
                      <button
                        onClick={handleWebsiteClick}
                        className="text-sm text-primary hover:underline"
                      >
                        {partner.website}
                      </button>
                    </div>
                  </div>
                )}
                
                {partner.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">E-mail</p>
                      <button
                        onClick={handleEmailClick}
                        className="text-sm text-primary hover:underline"
                      >
                        {partner.email}
                      </button>
                    </div>
                  </div>
                )}
                
                {partner.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Telefone</p>
                      <a
                        href={`tel:${partner.phone}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {partner.phone}
                      </a>
                    </div>
                  </div>
                )}
                
                {partner.social_media && (
                  <div className="flex items-center gap-3">
                    <Share2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Rede Social</p>
                      <a
                        href={partner.social_media}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {partner.social_media}
                      </a>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Descrição */}
          <div className="lg:col-span-2">
            <Card className="shadow-medium">
              <CardHeader>
                <CardTitle>Sobre a Empresa</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="text-muted-foreground leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: partner.description || '' }}
                />
              </CardContent>
            </Card>
          </div>

          {/* Vouchers */}
          <div className="mt-8">
            <PartnerVouchers 
              partnerId={partner.id} 
              partnerName={partner.name}
              partnerAddress={partner.address}
              partnerPhone={partner.phone}
              partnerEmail={partner.email}
              partnerLogo={partner.logo}
            />
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PartnerDetails;
